import type { GetCustomerVaultResponse } from "@/openapi/generated/models";

export const ACH_WEB_VAULT_TYPE = "ACH_WEB";

export const shouldSetAsDefault = (existingVaultCount: number): boolean => {
  return existingVaultCount === 0;
};

export function isAchWebVault(vault: { type?: string | null }): boolean {
  return vault.type === ACH_WEB_VAULT_TYPE;
}

const BANK_VAULT_TYPES = new Set([
  "ACH",
  ACH_WEB_VAULT_TYPE,
  "CHECKING",
  "SAVINGS",
]);

export function isBankVault(vault: { type?: string | null }): boolean {
  const type = vault.type?.trim().toUpperCase();
  return type != null && BANK_VAULT_TYPES.has(type);
}

export function bankAccountKindLabel(accountType: string | null | undefined): string {
  if (accountType === "SAVINGS") return "Savings";
  if (accountType === "CHECKING") return "Checking";
  return "Bank account";
}

export function bankAccountNameLabel(bankName: string | null | undefined): string {
  const name = bankName?.trim();
  return name || "Bank account";
}

export function bankAccountMetaLabel(
  accountType: string | null | undefined,
  bankName: string | null | undefined,
): string {
  return `${bankAccountKindLabel(accountType)} · ${bankAccountNameLabel(bankName)}`;
}

export function getPreferredVault(
  vaults: GetCustomerVaultResponse[] | null | undefined,
): GetCustomerVaultResponse | null {
  if (!vaults?.length) return null;
  return vaults.find((vault) => vault.isDefault === true) ?? vaults[0];
}

export const getVaultIdKey = (
  vaultId: GetCustomerVaultResponse["id"],
): string => String(vaultId);

export const vaultIdsEqual = (
  a: GetCustomerVaultResponse["id"],
  b: GetCustomerVaultResponse["id"],
): boolean => getVaultIdKey(a) === getVaultIdKey(b);

const isTruthyDefault = (value: unknown): boolean => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return false;
};

/** Ensures exactly one default when multiple vaults exist; a lone vault is always default. */
export const normalizeVaultDefaults = (
  vaults: GetCustomerVaultResponse[],
): GetCustomerVaultResponse[] => {
  if (vaults.length === 0) return vaults;

  if (vaults.length === 1) {
    return isTruthyDefault(vaults[0].isDefault)
      ? vaults
      : [{ ...vaults[0], isDefault: true }];
  }

  const markedDefault = vaults.filter((vault) => isTruthyDefault(vault.isDefault));
  if (markedDefault.length === 1) {
    const defaultVaultId = markedDefault[0].id;
    return vaults.map((vault) => ({
      ...vault,
      isDefault: vaultIdsEqual(vault.id, defaultVaultId),
    }));
  }

  const defaultVaultId =
    markedDefault[0]?.id ??
    [...vaults].sort((a, b) => {
      const aTime = a.createdAt?.getTime() ?? 0;
      const bTime = b.createdAt?.getTime() ?? 0;
      return aTime - bTime;
    })[0].id;

  return vaults.map((vault) => ({
    ...vault,
    isDefault: vaultIdsEqual(vault.id, defaultVaultId),
  }));
};
